import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Image,
  Icon,
  Button,
} from "@chakra-ui/react";
import { FaArrowRight } from "react-icons/fa";
import {
  getFemaleRatioData,
  formatFemaleRatioData,
} from "../../services/femaleRatioService";
import {
  getAverageAgeData,
  formatAverageAgeData,
} from "../../services/averageAgeService";

const images = ["/jpg/img1.jpeg", "/jpg/img2.jpeg", "/jpg/img3.jpeg"];

const JoinUsSection = () => {
  const [femaleRatioData, setFemaleRatioData] = useState(null);
  const [averageAgeData, setAverageAgeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch all dynamic data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch female ratio data
        const femaleRatioResult = await getFemaleRatioData();
        if (femaleRatioResult.success && femaleRatioResult.data) {
          const formatted = formatFemaleRatioData(femaleRatioResult.data);
          setFemaleRatioData(formatted);
        }

        // Fetch average age data
        const avgAgeResult = await getAverageAgeData();
        if (avgAgeResult.success && avgAgeResult.data) {
          const formatted = formatAverageAgeData(avgAgeResult.data);
          setAverageAgeData(formatted);
        }
      } catch (error) {
        console.error("Error fetching career stats data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Define stats data with dynamic values from APIs
  const stats = [
    {
      icon: "/png/female-icon.png",
      title: loading ? "..." : femaleRatioData?.percentage,
      desc: "Female Employee",
      large: true,
    },
    {
      icon: "/png/leave-approval.png",
      title: "In a day",
      desc: "Leave Approval",
      large: true,
    },
    {
      icon: "/png/avarage-age-icon.png",
      title: loading ? "..." : averageAgeData?.age,
      desc: "Average age",
      large: true,
    },
    {
      icon: "/png/employee-icon.png",
      title: "200+",
      desc: 'Employee',
      large: true
    },
    {
      icon: "/png/career-opurtunity-icon.png",
      title: "27",
      desc: "Years of Career Opportunities",
      large: true,
    },
  ];
  return (
    <Box py={{ base: 8, md: 16 }} bg="#f7f9fa">
      <Heading
        as="h2"
        fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
        textAlign="center"
        mb={{ base: 6, md: 8 }}
        color="#244034"
        fontWeight="bold"
      >
        Join Us
      </Heading>
      <Box
        display={{ base: "block", md: "flex" }}
        alignItems="flex-start"
        justifyContent="center"
        maxW="1200px"
        mx="auto"
        px={{ base: 2, md: 0 }}
      >
        {/* Left: Custom Image Grid */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gridTemplateRows={{ base: "auto", md: "1fr 1fr" }}
          gap={{ base: 4, md: 8 }}
          minW={{ base: "100%", md: "320px" }}
          maxW={{ base: "100%", md: "340px" }}
          mr={{ md: 10 }}
          mb={{ base: 8, md: 0 }}
        >
          {/* Left column: big image, spans both rows on desktop */}
          <Image
            src={images[0]}
            alt="img1"
            borderRadius="16px"
            objectFit="cover"
            gridRow={{ base: "1", md: "1 / span 2" }}
            gridColumn={{ base: "1", md: "1" }}
            w="100%"
            h={{ base: "180px", md: "370px" }}
            minW={{ base: "100%", md: "180px" }}
            minH={{ base: "180px", md: "370px" }}
            boxShadow="md"
          />
          {/* Right column: top image */}
          <Image
            src={images[1]}
            alt="img2"
            borderRadius="12px"
            objectFit="cover"
            gridRow={{ base: "2", md: "1" }}
            gridColumn={{ base: "1", md: "2" }}
            w="100%"
            h={{ base: "180px", md: "180px" }}
            minW={{ base: "100%", md: "140px" }}
            minH="180px"
            boxShadow="md"
          />
          {/* Right column: bottom image */}
          <Image
            src={images[2]}
            alt="img3"
            borderRadius="12px"
            objectFit="cover"
            gridRow={{ base: "3", md: "2" }}
            gridColumn={{ base: "1", md: "2" }}
            w="100%"
            h={{ base: "180px", md: "180px" }}
            minW={{ base: "100%", md: "140px" }}
            minH="180px"
            boxShadow="md"
          />
        </Box>
        {/* Right: Info Cards and Others */}
        <Box flex="1" minW={{ base: "100%", md: "340px" }}>
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3 }}
            spacingX={6}
            spacingY={6}
            mb={{ base: 4, md: 8 }}
            w="100%"
          >
            {stats.map((card, idx) => (
              <Box
                key={idx}
                bgImage="url('/png/career-join-us-card.png')"
                bgSize="cover"
                bgPosition="center"
                color="white"
                borderRadius="14px"
                p={{ base: 4, md: 6 }}
                w="100%"
                h={{ base: "140px", md: "160px" }}
                display="flex"
                flexDir="column"
                justifyContent="center"
                alignItems="flex-start"
                boxShadow="sm"
                position="relative"
                overflow="hidden"
                mx="auto"
              >
                <VStack
                  align="start"
                  spacing={1}
                  zIndex={2}
                  position="relative"
                  justify="center"
                  h="100%"
                >
                  <Image
                    src={card.icon}
                    alt={card.desc}
                    width="32px"
                    height="48px"
                    filter="invert(1) brightness(2) sepia(1) hue-rotate(20deg) saturate(1000%)"
                  />
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "lg", sm: "xl", md: "1.3rem" }}
                    color="#E6FF3F"
                  >
                    {card.title}
                  </Text>
                  <Text
                    fontSize={{ base: "sm", sm: "md", md: "lg" }}
                    color="white"
                  >
                    {card.desc}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
          {/* Why Join Us? Dropdown */}
          {/* <Box mb={{ base: 4, md: 8 }}>
            <HStack justify="space-between" w="full" borderBottom="1px solid #E5E7EB" pb={2}>
              <Text fontWeight="medium" color="#215560" fontSize={{ base: 'lg', md: 'xl' }}>Why Join Us?</Text>
              <Icon as={FaRegArrowAltCircleDown} boxSize={{ base: 7, md: 8 }} color="#215560" cursor="pointer" />
            </HStack>
          </Box> */}
          {/* See more button */}
        </Box>
      </Box>
      <HStack justify="center" mt={2} spacing={{ base: 2, md: 4 }}>
        <Button
          variant="outline"
          borderRadius="full"
          px={{ base: 6, md: 8 }}
          py={{ base: 2, md: 3 }}
          fontWeight="bold"
          fontSize={{ base: "lg", md: "xl" }}
          color="#215560"
          borderColor="#215560"
          bg="#fff"
          _hover={{ bg: "#E6E8F5", borderColor: "#215560", color: "#215560" }}
          onClick={() => {
            window.location.href = "/join-us";
          }}
        >
          See more
        </Button>
        <Button
          borderRadius="full"
          colorScheme="teal"
          bg="#215560"
          minW={{ base: 10, md: 12 }}
          minH={{ base: 10, md: 12 }}
          p={0}
          _hover={{ bg: "#17434a" }}
          onClick={() => {
            window.location.href = "/join-us";
          }}
        >
          <Icon as={FaArrowRight} color="white" boxSize={6} />
        </Button>
      </HStack>
    </Box>
  );
};

export default JoinUsSection;
